import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Link,
} from "@react-pdf/renderer";
import { addToast } from "@heroui/react";

import { Recommendation, UserJourneyData } from "@/types/dataSchema";
import { Answers } from "@/app/onboarding/AnswerContext";

interface PDFGenerationOptions {
  recommendations: Recommendation[];
  answers: Answers;
  journeyData?: UserJourneyData;
}

// Styles modernes pour le PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 0,
    fontFamily: "Helvetica",
  },
  header: {
    backgroundColor: "#5146ae", // CrowdSec purple
    padding: 30,
    marginBottom: 20,
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 16,
    color: "#e6e6ff",
  },
  container: {
    padding: 20,
  },
  infoCard: {
    backgroundColor: "#f0f8ff",
    border: "2px solid #2980b9",
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  infoText: {
    fontSize: 12,
    color: "#2c3e50",
    marginBottom: 5,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#5146ae",
    marginBottom: 10,
    marginTop: 20,
  },
  sectionDivider: {
    height: 3,
    backgroundColor: "#5146ae",
    marginBottom: 15,
  },
  summaryCard: {
    backgroundColor: "#fafafa",
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: 15,
    marginBottom: 20,
  },
  summaryText: {
    fontSize: 11,
    color: "#2c3e50",
    lineHeight: 1.6,
  },
  recommendationCard: {
    backgroundColor: "#ffffff",
    border: "2px solid #5146ae",
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    breakInside: "avoid",
    orphans: 3,
    widows: 3,
  },
  recommendationHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  recommendationNumber: {
    width: 30,
    height: 30,
    backgroundColor: "#5146ae",
    borderRadius: 15,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 15,
  },
  recommendationNumberText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "bold",
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2c3e50",
    flex: 1,
  },
  recommendationDescription: {
    fontSize: 11,
    color: "#6c757d",
    marginBottom: 8,
    lineHeight: 1.5,
  },
  benefitsTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#27ae60",
    marginBottom: 8,
    marginTop: 10,
  },
  benefitItem: {
    fontSize: 10,
    color: "#2c3e50",
    marginBottom: 4,
    paddingLeft: 15,
  },
  durationText: {
    fontSize: 10,
    color: "#f39c12",
    fontWeight: "bold",
    marginTop: 8,
  },
  actionsTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#2980b9",
    marginBottom: 8,
    marginTop: 12,
  },
  actionItem: {
    fontSize: 10,
    color: "#2980b9",
    marginBottom: 6,
    paddingLeft: 15,
  },
  actionLink: {
    fontSize: 10,
    color: "#2980b9",
    textDecoration: "underline",
    marginBottom: 6,
    paddingLeft: 15,
  },
  actionDescription: {
    fontSize: 9,
    color: "#6c757d",
    paddingLeft: 25,
    marginBottom: 4,
  },
  finalCard: {
    backgroundColor: "#f5fafe",
    border: "2px solid #2980b9",
    borderRadius: 8,
    padding: 20,
    marginTop: 20,
  },
  finalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#5146ae",
    marginBottom: 15,
  },
  finalText: {
    fontSize: 11,
    color: "#2c3e50",
    lineHeight: 1.6,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: "1px solid #ddd",
    paddingTop: 10,
  },
  footerText: {
    fontSize: 8,
    color: "#6c757d",
  },
  footerLink: {
    fontSize: 8,
    color: "#2980b9",
    textDecoration: "underline",
  },
  pageNumber: {
    fontSize: 8,
    color: "#6c757d",
  },
  // Journey Summary styles
  journeySection: {
    marginTop: 20,
    marginBottom: 20,
  },
  journeyCard: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e9ecef",
    borderRadius: 6,
    padding: 15,
    marginBottom: 15,
    breakInside: "avoid",
    orphans: 2,
    widows: 2,
  },
  journeyTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2c3e50",
    marginBottom: 8,
  },
  journeyQuestion: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#495057",
    marginBottom: 4,
  },
  journeyAnswer: {
    fontSize: 10,
    color: "#6c757d",
    marginBottom: 8,
    paddingLeft: 10,
  },
  journeyStatus: {
    fontSize: 8,
    color: "#28a745",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
});

// Composant PDF principal
const PDFDocument: React.FC<{
  answers?: Answers;
  journeyData?: UserJourneyData;
  recommendations: Recommendation[];
  timestamp: string;
}> = ({ answers, journeyData, recommendations, timestamp }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header spectaculaire */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>CrowdSec</Text>
        <Text style={styles.headerSubtitle}>
          Security Recommendations Report
        </Text>
      </View>

      <View style={styles.container}>
        {/* Carte d'information */}
        <View style={styles.infoCard}>
          <Text style={styles.infoText}>Generated on {timestamp}</Text>
          <Text style={styles.infoText}>
            {recommendations.length} personalized security recommendations
          </Text>
        </View>{" "}
        {/* Journey Summary Section */}
        {answers && journeyData && (
          <View style={styles.journeySection}>
            <Text style={styles.sectionTitle}>Your Security Journey</Text>
            <View style={styles.sectionDivider} />

            {Object.entries(answers).map(([questionId, answer]) => {
              const question = journeyData.questions.find(
                (q) => q.id === questionId,
              );

              if (!question) return null;

              return (
                <View key={questionId} style={styles.journeyCard}>
                  <Text style={styles.journeyQuestion}>
                    {question.question}
                  </Text>
                  <View style={styles.journeyStatus}>
                    <Text style={styles.journeyStatus}>✓ COMPLETED</Text>
                  </View>
                  {Array.isArray(answer) ? (
                    answer.map((ans, index) => {
                      const option = question.options.find(
                        (opt) => opt.value === ans,
                      );

                      return (
                        <Text key={index} style={styles.journeyAnswer}>
                          • {option?.label || ans}
                        </Text>
                      );
                    })
                  ) : (
                    <Text style={styles.journeyAnswer}>
                      {"• "}
                      {question.options.find((opt) => opt.value === answer)
                        ?.label || answer}
                    </Text>
                  )}
                </View>
              );
            })}
          </View>
        )}
        {/* Executive Summary */}
        <Text style={styles.sectionTitle}>Executive Summary</Text>
        <View style={styles.sectionDivider} />
        <View style={styles.summaryCard}>
          <Text style={styles.summaryText}>
            Based on your comprehensive security assessment, our analysis has
            identified {recommendations.length} strategic recommendations
            specifically tailored to strengthen your cybersecurity posture.
            {"\n\n"}
            These recommendations have been carefully selected to address your
            organization&apos;s unique risk profile, infrastructure
            requirements, and security objectives. Each recommendation includes
            detailed implementation guidance, expected benefits, and estimated
            deployment time.
          </Text>
        </View>
        {/* Recommendations Section */}
        <Text style={styles.sectionTitle}>
          Recommended Security Recommendations
        </Text>
        <View style={styles.sectionDivider} />
        {/* Toutes les recommendations sur la même page */}
        {recommendations.map((recommendation, index) => (
          <View
            key={recommendation.id || index}
            style={styles.recommendationCard}
          >
            {/* Header de recommendation */}
            <View style={styles.recommendationHeader}>
              <View style={styles.recommendationNumber}>
                <Text style={styles.recommendationNumberText}>{index + 1}</Text>
              </View>
              <Text style={styles.recommendationTitle}>
                {recommendation.name}
              </Text>
            </View>

            {/* Description */}
            <Text style={styles.recommendationDescription}>
              {recommendation.shortDescription}
            </Text>

            {recommendation.description &&
              recommendation.description !==
                recommendation.shortDescription && (
                <Text style={styles.recommendationDescription}>
                  {recommendation.description}
                </Text>
              )}

            {/* Benefits */}
            {recommendation.benefits && recommendation.benefits.length > 0 && (
              <View>
                <Text style={styles.benefitsTitle}>Key Benefits:</Text>
                {recommendation.benefits.slice(0, 3).map((benefit, idx) => (
                  <Text key={idx} style={styles.benefitItem}>
                    • {benefit}
                  </Text>
                ))}
                {recommendation.benefits.length > 3 && (
                  <Text style={styles.benefitItem}>
                    + {recommendation.benefits.length - 3} more benefits
                  </Text>
                )}
              </View>
            )}

            {/* Duration */}
            {recommendation.durationInMinutes && (
              <Text style={styles.durationText}>
                Implementation time:{" "}
                {recommendation.durationInMinutes > 60
                  ? `${Math.ceil(recommendation.durationInMinutes / 60)}h`
                  : `${recommendation.durationInMinutes} min`}
              </Text>
            )}

            {/* Primary Action */}
            {recommendation.primaryAction && (
              <View>
                <Text style={styles.actionsTitle}>Primary Action:</Text>
                <View>
                  {recommendation.primaryAction.url ? (
                    <Link
                      src={recommendation.primaryAction.url}
                      style={styles.actionLink}
                    >
                      {recommendation.primaryAction.ctaLabel || "Learn More"}
                    </Link>
                  ) : (
                    <Text style={styles.actionItem}>
                      {recommendation.primaryAction.ctaLabel ||
                        "Contact Support"}
                    </Text>
                  )}
                </View>
              </View>
            )}
          </View>
        ))}
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          © CrowdSec - Generated by The CrowdSec Kickstart
        </Text>
        <Link src="https://start.crowdsec.net" style={styles.footerLink}>
          https://start.crowdsec.net
        </Link>
        <Text
          fixed
          render={({ pageNumber }) => `Page ${pageNumber}`}
          style={styles.pageNumber}
        />
      </View>
    </Page>

    {/* Page finale avec support */}
    <Page size="A4" style={styles.page}>
      <View style={styles.container}>
        <View style={styles.finalCard}>
          <Text style={styles.finalTitle}>Next Steps & Support</Text>
          <Text style={styles.finalText}>
            This comprehensive security assessment report provides you with
            actionable recommendations tailored to your specific environment. We
            recommend prioritizing recommendations based on your immediate
            security needs and available resources.
            {"\n\n"}
            For additional support, implementation guidance, or questions about
            these recommendations, visit our community resources or contact our
            security experts.
          </Text>
        </View>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          © CrowdSec - Generated by The CrowdSec Kickstart
        </Text>
        <Link src="https://start.crowdsec.net" style={styles.footerLink}>
          https://start.crowdsec.net
        </Link>
        <Text
          fixed
          render={({ pageNumber }) => `Page ${pageNumber}`}
          style={styles.pageNumber}
        />
      </View>
    </Page>
  </Document>
);

export const generateRecommendationsPDF = async ({
  recommendations,
  answers,
  journeyData,
}: PDFGenerationOptions): Promise<void> => {
  try {
    const now = new Date();
    const timestamp = now.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Génération du PDF avec @react-pdf/renderer
    const blob = await pdf(
      <PDFDocument
        answers={answers}
        journeyData={journeyData}
        recommendations={recommendations}
        timestamp={timestamp}
      />,
    ).toBlob();

    // Nom de fichier avec timestamp
    const filename = `crowdsec-security-recommendations-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}h${String(now.getMinutes()).padStart(2, "0")}.pdf`;

    // Téléchargement du fichier
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Notification de succès
    addToast({
      title: "🎨 Report generated!",
      description: `A short summary report has been downloaded as ${filename}`,
      color: "success",
    });
  } catch {
    addToast({
      title: "PDF Generation Failed",
      description:
        "An error occurred while generating the PDF report. Please try again.",
      color: "danger",
    });
  }
};
